import { formatDate } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { locale } from 'moment';
import { MenuItem } from 'primeng/api';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  AgendaDecision,
  AgendaDecisionStatus,
  ArborWebcastData,
  CabinetRegistration,
  DateTimeData,
  Document,
  MarkActionActor,
  MeetingAdditionalInfo,
  MeetingItem,
  MeetingItemSummary,
  MeetingOptionsMenuActions,
  MeetingPrimaryActions,
  MeetingPublicationFolder,
  MeetingSidebar,
  RoyalCastWebcastData,
  SignDecreesData,
  SignDecreesValidateData,
  VotingLetterSettings
} from '../store/meeting.models';
import {
  ChangeDateTimeRes,
  ClosableDateOption,
  IComment,
  ICommentSection,
  ICommentShareOption,
  IDeleteMeetingDetails,
  IFetchTemplateNameRes,
  IGeneratePublication,
  IGeneratePublicationListItem,
  IMarkActionRes,
  IMarkActionUser,
  ISubmitMarkActionData,
  MeetingContentCriteria,
  MeetingHasVotingLetters,
  MeetingVotingLetter,
  NotesTab,
  TreatmentTimeData
} from '../store/meeting.types';

import { RegisterVotes } from '~/meeting/ui/actions/add-voting-results/add-voting-results.types';
import { ITemplate } from '~/meeting/ui/actions/generate-publication/generate-publication.types';
import { MailUserPayload, MailUserResponse } from '~/meeting/ui/actions/mail-dialog/models/mail-user-payload.model';
import { ListOption } from '~/repositories/widgets/store/widgets.models';
import { DetailHeader } from '~/ui/detail/detail-header/detail-header.types';
import { PANELMENUTYPES } from '~/ui/panel-menu/panel-menu.const';
import { VotingResult } from '~/voting/voting.types';

@Injectable()
export class MeetingService {
  constructor(private readonly http: HttpClient) {}

  public fetchMeetingHeader(id: string): Observable<DetailHeader> {
    return this.http.get<DetailHeader>(`${environment.BACKEND_URL}/meetings/${id}`);
  }

  public updateMeetingHeader(id: string, data: DetailHeader): Observable<DetailHeader> {
    return this.http.put<DetailHeader>(`${environment.BACKEND_URL}/meetings/${id}`, data);
  }

  public fetchMeetingContent(id: string, criteria: MeetingContentCriteria): Observable<MeetingItem[]> {
    return this.http.post<MeetingItem[]>(`${environment.BACKEND_URL}/meetings/${id}/meetingstructure`, criteria);
  }

  public fetchMeetingItemSummary(id: string): Observable<MeetingItemSummary> {
    return this.http.get<MeetingItemSummary>(`${environment.BACKEND_URL}/meetings/summary/${id}`);
  }

  public fetchMeetingSidebar(id: string): Observable<MeetingSidebar> {
    return this.http.get<MeetingSidebar>(`${environment.BACKEND_URL}/meetings/${id}/sidebar`);
  }

  public fetchMeetingPrimaryActions(id: string): Observable<MeetingPrimaryActions> {
    return this.http.get<MeetingPrimaryActions>(`${environment.BACKEND_URL}/meetings/${id}/primarymenu`);
  }

  public fetchMeetingOptionsMenu(id: string): Observable<MeetingOptionsMenuActions> {
    return this.http.get<MeetingOptionsMenuActions>(`${environment.BACKEND_URL}/meetings/${id}/optionsmenu`);
  }

  public fetchDeleteMeetingDetails(id: string): Observable<IDeleteMeetingDetails> {
    return this.http.get<IDeleteMeetingDetails>(`${environment.BACKEND_URL}/meetings/${id}/delete`);
  }

  public deleteMeeting(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/meetings/${id}/delete`);
  }

  public fetchMeetingAdditionalInfo(id: string): Observable<MeetingAdditionalInfo> {
    return this.http.get<MeetingAdditionalInfo>(`${environment.BACKEND_URL}/meetings/${id}/additional-info`);
  }

  public updateMeetingAdditionalInfo(
    meetingId: string,
    additionalInfo: MeetingAdditionalInfo
  ): Observable<MeetingAdditionalInfo> {
    if (!meetingId) {
      return null;
    }

    return this.http.post<MeetingAdditionalInfo>(
      `${environment.BACKEND_URL}/meetings/${meetingId}/additional-info`,
      additionalInfo
    );
  }

  public fetchMeetingAttachments(id: string): Observable<Document[]> {
    return this.http.get<Document[]>(`${environment.BACKEND_URL}/meetings/${id}/additional-info/attachments`);
  }

  public removeMeetingAttachment(meetingId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/meetings/${meetingId}/additional-info/${attachmentId}`);
  }

  public validateVotingLetters(votingLetterSettings: VotingLetterSettings): Observable<string[]> {
    return this.http.post<string[]>(`${environment.BACKEND_URL}/voting/letter/generate/validate`, votingLetterSettings);
  }

  public generateVotingLetters(votingLetterSettings: VotingLetterSettings): Observable<VotingLetterSettings> {
    return this.http.post<VotingLetterSettings>(
      `${environment.BACKEND_URL}/voting/letter/generate/bulk`,
      votingLetterSettings
    );
  }

  public registerCabinetPreparation(cabinetRegistration: CabinetRegistration): Observable<CabinetRegistration> {
    return this.http.post<CabinetRegistration>(
      `${environment.BACKEND_URL}/meetings/cabinet-preparation/register`,
      cabinetRegistration
    );
  }

  public fetchCabinetRegistrationOptions(): Observable<MenuItem[]> {
    return this.http.get<MenuItem[]>(`${environment.BACKEND_URL}/meetings/cabinet-preparation/options`);
  }

  public fetchMoveMeetingItemOrgans(meetingId: string): Observable<ListOption[]> {
    return this.http.get<ListOption[]>(`${environment.BACKEND_URL}/meetings/${meetingId}/move/organ-options`);
  }

  public fetchMoveMeetingItemDates(meetingId: string, organId: string): Observable<ClosableDateOption[]> {
    const params: any = {};
    if (organId) {
      params.organId = organId;
    }
    return this.http.get<ClosableDateOption[]>(`${environment.BACKEND_URL}/meetings/${meetingId}/move/date-options`, {
      params
    });
  }

  public moveMeetingItem(
    meetingId: string,
    organId: string,
    urgent: boolean,
    motivation: string,
    checkedMeetingItems: MeetingItem[]
  ): Observable<void> {
    const checkedMeetingItemIds = this.findAgendaPageIds(checkedMeetingItems);
    return this.http.post<void>(`${environment.BACKEND_URL}/meetings/${meetingId}/move`, {
      organId,
      urgent,
      motivation,
      checkedMeetingItemIds
    });
  }

  private findAgendaPageIds(meetingItems: MeetingItem[]): string[] {
    return meetingItems.filter((item) => item.type === 'AGENDAPAGE' && !item.concept).map((item) => item.key);
  }

  public takeAgendaDecision(meetingId: string, agendaDecision: AgendaDecision): Observable<AgendaDecision> {
    return this.http.post<AgendaDecision>(`${environment.BACKEND_URL}/meetings/${meetingId}/decision`, agendaDecision);
  }

  public fetchAgendaDecisionOptions(): Observable<AgendaDecisionStatus[]> {
    return this.http.get<AgendaDecisionStatus[]>(`${environment.BACKEND_URL}/meetings/decision-options`);
  }

  public fetchAgendaDecisionDateOptions(meetingId: string, unscheduledPostpone: boolean): Observable<ListOption[]> {
    return this.http.get<ListOption[]>(`${environment.BACKEND_URL}/meetings/${meetingId}/postpone-options`, {
      params: { unscheduledPostpone }
    });
  }

  public fetchAgendaPagesHaveVotingLetter(selectedIds: string[]): Observable<MeetingHasVotingLetters> {
    return this.http.post<MeetingHasVotingLetters>(`${environment.BACKEND_URL}/voting/result/register/validate`, {
      selectedIds
    });
  }

  public fetchAgendaPagesVotingLetter(votingLetterIds: string[]): Observable<MeetingVotingLetter> {
    return this.http.post<MeetingVotingLetter>(`${environment.BACKEND_URL}/voting/result/register/formdata`, {
      votingLetterIds
    });
  }

  public updateVotingLetterResult(objectMetaId: string, votingLetter: RegisterVotes): Observable<void> {
    return this.http.post<void>(`${environment.BACKEND_URL}/voting/result/register/bulk`, votingLetter);
  }

  public fetchMarkActionActors(meetingId: string, type: string): Observable<MarkActionActor[]> {
    return this.http.get<ListOption[]>(`${environment.BACKEND_URL}/meetings/${meetingId}/markactions/${type}/actors`);
  }

  public fetchMarkActionData(meetingId: string, type: string, selectedIds: string[]): Observable<IMarkActionRes[]> {
    return this.http.post<IMarkActionRes[]>(
      `${environment.BACKEND_URL}/meetings/${meetingId}/markactions/${type}/data`,
      selectedIds
    );
  }

  public submitMarkActionActors(meetingId: string, type: string, data: ISubmitMarkActionData): Observable<void> {
    return this.http.post<void>(`${environment.BACKEND_URL}/meetings/${meetingId}/markactions/${type}`, data.data);
  }

  public fetchVotingResult(meetingItemId: string): Observable<VotingResult[]> {
    return this.http.get<VotingResult[]>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/votingresults`);
  }

  public createArborWebcast(meetingId: string, data: ArborWebcastData): Observable<ArborWebcastData> {
    return this.http.post<ArborWebcastData>(`${environment.BACKEND_URL}/meetings/${meetingId}/arbor-webcast`, data);
  }

  public updateArborWebcast(meetingId: string, data: ArborWebcastData): Observable<ArborWebcastData> {
    return this.http.put<ArborWebcastData>(`${environment.BACKEND_URL}/meetings/${meetingId}/arbor-webcast`, data);
  }

  public createRoyalWebcast(meetingId: string, data: RoyalCastWebcastData): Observable<Date> {
    return this.http.post<Date>(`${environment.BACKEND_URL}/meetings/${meetingId}/royalcast-webcast`, data);
  }

  public getNotes(meetingItemId: string): Observable<NotesTab[]> {
    return this.http.get<NotesTab[]>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/notes`);
  }

  public updateNotes(meetingItemId: string, data: NotesTab[]): Observable<NotesTab[]> {
    return this.http.post<NotesTab[]>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/notes`, data);
  }

  public releaseAgendaPoints(
    meetingId: string,
    checkedMeetingItems: MeetingItem[],
    signer: string,
    additionalSigners: string[]
  ): Observable<void> {
    const checkedMeetingItemIds = this.findAgendaPageIds(checkedMeetingItems);
    return this.http.post<void>(`${environment.BACKEND_URL}/meetings/${meetingId}/release`, {
      checkedMeetingItemIds,
      signer,
      additionalSigners
    });
  }

  public validateDecrees(checkedMeetingItems: MeetingItem[]): Observable<SignDecreesValidateData> {
    const checkedMeetingItemIds = this.findAgendaPageIds(checkedMeetingItems);
    return this.http.post<SignDecreesValidateData>(
      `${environment.BACKEND_URL}/sign/publications/validate`,
      checkedMeetingItemIds
    );
  }

  public signDecrees(signDecreesData: SignDecreesData): Observable<SignDecreesData> {
    return this.http.post<SignDecreesData>(`${environment.BACKEND_URL}/sign/publications`, signDecreesData);
  }

  public postComment(meetingItemId: string, comment: IComment): Observable<IComment> {
    return this.http.post<IComment>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/comments`, comment);
  }

  public updateComment(meetingItemId: string, comment: IComment): Observable<IComment> {
    return this.http.put<IComment>(
      `${environment.BACKEND_URL}/meetingitems/${meetingItemId}/comments/${comment.id}`,
      comment
    );
  }

  public removeComment(meetingItemId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/comments/${commentId}`);
  }

  public fetchComments(meetingItemId: string): Observable<ICommentSection[]> {
    return this.http.get<ICommentSection[]>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/comments`);
  }

  public getCommentShareWith(meetingItemId: string): Observable<ICommentShareOption[]> {
    return this.http.get<ICommentShareOption[]>(
      `${environment.BACKEND_URL}/meetingitems/${meetingItemId}/comments/sharewith`
    );
  }

  public fetchPublications(meetingId: string) {
    return this.http.get<MeetingPublicationFolder[]>(`${environment.BACKEND_URL}/meetings/${meetingId}/publications`);
  }

  public fetchTemplates(context: string, id: string, checkedMeetingItems: MeetingItem[]) {
    const hasAgendaPagesSelected: boolean = !!checkedMeetingItems?.some(
      (item) => item.type === PANELMENUTYPES.AGENDAPAGE && !item.acceptable
    );
    return this.http.post<ITemplate[]>(`${environment.BACKEND_URL}/publications/templates/search`, {
      context: context,
      id: id,
      selectedItems: hasAgendaPagesSelected
    });
  }

  public fetchSignerOptions(): Observable<ListOption[]> {
    return this.http.get<ListOption[]>(`${environment.BACKEND_URL}/signers/find`, {
      params: { module: 'EDECISION' }
    });
  }

  public fetchTemplateName(id: string, templateId: string): Observable<IFetchTemplateNameRes> {
    return this.http.post<IFetchTemplateNameRes>(`${environment.BACKEND_URL}/publications/title`, {
      templateId,
      id
    });
  }

  public generatePublication(generatePublicationData: IGeneratePublication) {
    return this.http.post<IGeneratePublicationListItem[]>(
      `${environment.BACKEND_URL}/publications/${generatePublicationData.type}`,
      generatePublicationData.data
    );
  }

  public changeDateTime(meetingId: string, data: DateTimeData): Observable<void> {
    return this.http.patch<void>(`${environment.BACKEND_URL}/meetings/${meetingId}/datetime`, data);
  }

  public validateDateTime(meetingId: string, data: DateTimeData): Observable<ChangeDateTimeRes> {
    return this.http.patch<ChangeDateTimeRes>(
      `${environment.BACKEND_URL}/meetings/${meetingId}/validatedatetime`,
      data
    );
  }

  public fetchMarkActionUsers(meetingItemId: string, type: string): Observable<IMarkActionUser[]> {
    return this.http.get<IMarkActionUser[]>(
      `${environment.BACKEND_URL}/meetingitems/${meetingItemId}/markactions/${type}/users`
    );
  }

  public getTreatmentTime(meetingItemId: string): Observable<TreatmentTimeData> {
    return this.http.get<TreatmentTimeData>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/treatmenttime`);
  }

  public changeTreatmentTime(meetingItemId: string, data: DateTimeData, force: boolean): Observable<ChangeDateTimeRes> {
    return this.http.post<ChangeDateTimeRes>(`${environment.BACKEND_URL}/meetingitems/${meetingItemId}/treatmenttime`, {
      startTime: data.startDate ? formatDate(data.startDate, 'HH:mm', locale()) : null,
      endTime: data.endDate ? formatDate(data.endDate, 'HH:mm', locale()) : null,
      force
    });
  }

  fetchMailUsers = (meetingId: string, agendaActionType: string, payload: MailUserPayload) => {
    const params = new HttpParams({
      fromObject: {
        pageSize: payload.pageSize,
        page: payload.page,
        searchTerm: payload.searchTerm
      }
    });
    return this.http.get<MailUserResponse>(
      `${environment.BACKEND_URL}/meetings/${meetingId}/action/${agendaActionType}/filter-users`,
      { params }
    );
  };
}
